import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AppProvider } from './contexts/AppContext';
import AuthLayout from './components/layout/AuthLayout';
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';

// 懒加载页面组件
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const InitializePasswordPage = React.lazy(() => import('./pages/auth/InitializePasswordPage'));
const VerifyOTPPage = React.lazy(() => import('./pages/auth/VerifyOTPPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage'));
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const URLMonitorPage = React.lazy(() => import('./pages/monitor/URLMonitorPage'));
const MonitorLogsPage = React.lazy(() => import('./pages/monitor/MonitorLogsPage'));
const NotificationsPage = React.lazy(() => import('./pages/notifications/NotificationsPage'));
const NotificationLogsPage = React.lazy(() => import('./pages/notifications/NotificationLogsPage'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const UserManagementPage = React.lazy(() => import('./pages/admin/UserManagementPage'));
const AdminRoute = React.lazy(() => import('./components/common/AdminRoute'));

// 配置全局消息
message.config({
  duration: 3,
  maxCount: 3,
  prefixCls: 'menshen-message',
});

const App: React.FC = () => {
  return (
    <ConfigProvider 
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
          fontSize: 14,
        },
        components: {
          Layout: {
            bodyBg: '#f5f5f5',
            siderBg: '#001529',
          },
          Menu: {
            darkItemBg: '#001529',
            darkSubMenuItemBg: '#000c17',
            darkItemSelectedBg: '#1890ff',
          },
        },
      }}
    >
      <AppProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* 认证相关路由 */}
              <Route path="/auth" element={<AuthLayout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="initialize-password" element={<InitializePasswordPage />} />
                <Route path="verify-otp" element={<VerifyOTPPage />} />
                <Route path="verify-email" element={<VerifyEmailPage />} />
                <Route path="" element={<Navigate to="/auth/login" replace />} />
              </Route>

              {/* 主应用路由 */}
              <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="monitor">
                  <Route path="urls" element={<URLMonitorPage />} />
                  <Route path="logs" element={<MonitorLogsPage />} />
                  <Route path="" element={<Navigate to="/monitor/urls" replace />} />
                </Route>
                <Route path="notifications">
                  <Route path="config" element={<NotificationsPage />} />
                  <Route path="logs" element={<NotificationLogsPage />} />
                  <Route path="" element={<Navigate to="/notifications/config" replace />} />
                </Route>
                <Route path="profile" element={<ProfilePage />} />
                <Route path="admin" element={<AdminRoute />}>
                  <Route path="users" element={<UserManagementPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="" element={<Navigate to="/admin/users" replace />} />
                </Route>
              </Route>

              {/* 404页面 */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </AppProvider>
    </ConfigProvider>
  );
};

export default App;